import React from "react";
import { Box, IconButton, Collapse, Tooltip, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function ChartCardWrapper({ id, label, isHighlighted, highlightColor, collapsed, onToggle, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: transform ? CSS.Transform.toString(transform) : undefined,
        transition: transition || undefined,
      }}
      sx={{
        opacity: isDragging ? 0.45 : 1,
        position: "relative",
        borderRadius: 1,
        outline: isHighlighted
          ? "2px solid"
          : highlightColor
          ? "2px solid"
          : "none",
        outlineColor: isHighlighted
          ? "primary.main"
          : highlightColor
          ? (highlightColor.main || highlightColor)
          : undefined,
        outlineOffset: "1px",
      }}
    >
      {/* Control bar — always visible, tiny */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: 22,
          px: 0.5,
          gap: 0.25,
          borderRadius: collapsed ? 1 : "4px 4px 0 0",
          bgcolor: isHighlighted
            ? "primary.dark"
            : highlightColor
            ? (highlightColor.dark || highlightColor)
            : collapsed
            ? "action.selected"
            : "transparent",
          opacity: collapsed ? 1 : 0.6,
          transition: "opacity 0.15s",
          "&:hover": { opacity: 1 },
          cursor: "default",
        }}
      >
        {/* Drag handle */}
        <Tooltip title="Drag to reorder" placement="right" arrow>
          <IconButton
            size="small"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
            sx={{
              p: 0.25,
              cursor: "grab",
              "&:active": { cursor: "grabbing" },
              color: "#fff",
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>

        {/* Label shown only when collapsed */}
        {collapsed && (
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: "0.65rem",
              color: "text.primary",
              lineHeight: 1,
              userSelect: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
        )}

        {/* Collapse / expand toggle */}
        <Tooltip
          title={collapsed ? "Expand" : "Collapse"}
          placement="left"
          arrow
        >
          <IconButton
            size="small"
            aria-label={collapsed ? "Expand" : "Collapse"}
            onClick={onToggle}
            sx={{
              p: 0.25,
              ml: "auto",
              color: "#fff",
            }}
          >
            {collapsed ? (
              <ExpandMoreIcon sx={{ fontSize: 13 }} />
            ) : (
              <ExpandLessIcon sx={{ fontSize: 13 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Card content */}
      <Collapse in={!collapsed} timeout={150}>
        {children}
      </Collapse>
    </Box>
  );
}

export default ChartCardWrapper;
